import React, { useReducer, createContext, useContext } from "react";
import { initialState, AuthReducer } from "./reducer";
import { Action, State } from "./types";
export const AuthStateContext = createContext<State>({
  user: { refreshToken: "" },
  isAuthenticated: false,
  loading: true,
  authType: "basic",
});
const AuthDispatchContext = createContext<React.Dispatch<Action>>(() => null);

/**
 * Retrieves the authentication state from context.
 *
 * @returns The authentication state context.
 */
export function useAuthState() {
  const context = useContext(AuthStateContext);
  if (context === undefined) {
    throw new Error("useAuthState must be used within a AuthProvider");
  }

  return context;
}

/**
 * Returns the authentication dispatch context used for updating authentication state.
 *
 * @returns The authentication dispatch context.
 */
export function useAuthDispatch() {
  const context = useContext(AuthDispatchContext);
  if (context === undefined) {
    throw new Error("useAuthDispatch must be used within a AuthProvider");
  }

  return context;
}

/**
 * Provides authentication context to its child components.
 *
 * @param {object} props - The component props.
 * @param {JSX.Element[]} props.children - The child components that will receive authentication context.
 * @returns {JSX.Element} The authentication provider component with its context.
 */
export const AuthProvider = ({ children }: { children: JSX.Element[] }) => {
  const [state, dispatch] = useReducer(AuthReducer, initialState);

  return (
    <AuthStateContext.Provider value={state}>
      <AuthDispatchContext.Provider value={dispatch}>
        {children}
      </AuthDispatchContext.Provider>
    </AuthStateContext.Provider>
  );
};
